import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Zap, ShieldCheck, BarChart3, Cpu, ArrowRight, Calculator, IndianRupee, Wrench, Clock, CheckCircle2, ShoppingCart, TrendingDown, Building2, Stethoscope, GraduationCap, Home, UtensilsCrossed, Truck, BatteryCharging, Sun } from 'lucide-react';
import { trackEvent, EVENTS } from '@/lib/analytics';
import { SEO, ORGANIZATION_SCHEMA } from '@/components/SEO';
import { estimateMonthlySavings, formatINRCompact, DEFAULTS } from '@/lib/savings';
import { AssumptionsFooter } from '@/components/AssumptionsDrawer';

const WHY_ITEMS = [
  { icon: Zap, title: 'Replace Diesel Generators', desc: 'Clean, silent and efficient energy storage to replace noisy, polluting DG sets.' },
  { icon: ShieldCheck, title: 'Zero-Emission Backup', desc: 'Compact footprint with zero emissions. Safe and environment-friendly power.' },
  { icon: BarChart3, title: 'No Energy Loss', desc: 'Dependable performance every time with 90%+ round-trip efficiency.' },
  { icon: Cpu, title: 'Future-Ready Monitoring', desc: 'Digital, data-driven energy management system with remote monitoring.' },
];

const CORE_SOLUTIONS = [
  {
    title: 'DG Replacement',
    desc: 'Replace noisy, polluting diesel gensets with clean, silent battery power. Save 30–60% on fuel costs while eliminating emissions.',
    image: '/images/voltide-battery-ev-charger.png',
    link: '/solutions/dg-replacement',
    savings: estimateMonthlySavings(500),
    primary: true,
  },
  {
    title: 'Solar + Storage',
    desc: 'Maximize your solar investment. Store excess generation during the day and use it when the sun goes down or during peak hours.',
    image: '/images/voltide-solar-ev.png',
    link: '/solutions/solar-storage',
    savings: estimateMonthlySavings(500),
    primary: false,
  },
];

const SECTORS = [
  { title: 'Restaurants', icon: UtensilsCrossed, image: '/images/voltide-restaurant.png', link: '/solutions/restaurants' },
  { title: 'Hospitals', icon: Stethoscope, image: '/images/voltide-hospital.png', link: '/solutions/hospitals' },
  { title: 'Universities', icon: GraduationCap, image: '/images/voltide-university.png', link: '/solutions/campuses' },
  { title: 'Warehouses', icon: Building2, image: '/images/voltide-battery-pack-large.png', link: '/solutions/warehouses' },
  { title: 'Apartments', icon: Home, image: '/images/voltide-apartment.png', link: '/solutions/apartments' },
  { title: 'EV Charging', icon: BatteryCharging, image: '/images/voltide-solar-ev.png', link: '/solutions/ev-charging' },
];

const BENEFITS = [
  { icon: IndianRupee, title: 'Low Upfront Cost', desc: 'Convert Capex to predictable Opex with flexible rental models.' },
  { icon: Clock, title: 'Rapid Deployment', desc: 'Fast installation and commissioning with no maintenance hassle.' },
  { icon: Wrench, title: 'Fully Managed Service', desc: 'End-to-end managed service including monitoring, maintenance, and support.' },
];

const STEPS = [
  { num: '01', title: 'Assessment', desc: 'Our experts evaluate your energy needs and site requirements.' },
  { num: '02', title: 'Choose Your BESS', desc: 'Select the optimal system size and configuration.' },
  { num: '03', title: 'Deployment', desc: 'Fast installation and commissioning at your site.' },
  { num: '04', title: 'Operate & Extend', desc: 'Stay powered with flexible long-term rentals and full support.' },
];

export default function HomePage() {
  return (
    <div data-testid="home-page">
      <SEO
        path="/"
        description="Battery Energy Storage Systems (BESS) for rent or purchase. Replace diesel generators with clean, silent, cost-effective battery power across India."
        schema={ORGANIZATION_SCHEMA}
      />

      {/* Trust Band */}
      <section className="border-b border-stone-100 bg-white/60 backdrop-blur-sm" data-testid="trust-band">
        <div className="voltide-container py-3">
          <div className="flex items-center justify-between gap-6 text-xs text-[#57534E] overflow-x-auto">
            <div className="flex items-center gap-6 shrink-0">
              <span className="flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> Live deployments across India</span>
              <span className="flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5 text-[#0061DD]" /> 24/7 remote monitoring</span>
              <span className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5 text-amber-500" /> 99.5% uptime SLA</span>
            </div>
            <Link to="/case-studies/uru" className="text-[#0061DD] hover:underline font-medium shrink-0" data-testid="trust-band-uru">
              See Uru Brewpark case study
            </Link>
          </div>
        </div>
      </section>

      {/* Hero */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        <div className="voltide-container py-16 lg:py-24">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <div className="animate-fade-up">
              <Badge className="rounded-full px-4 py-1.5 text-xs font-medium bg-blue-50 text-[#0061DD] border-blue-100 mb-6">
                Battery Energy Storage Systems
              </Badge>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-[#1A1A1A] leading-[1.1] mb-6">
                Clean, reliable energy storage
                <span className="blue-gradient-text"> — rent or buy</span>
              </h1>
              <p className="text-base lg:text-lg text-[#57534E] leading-relaxed mb-8 max-w-xl">
                Rent or buy modular BESS for commercial, residential and industrial needs. Replace diesel generators with silent, zero-emission power.
              </p>
              <div className="flex flex-wrap gap-3">
                <Link to="/quote" onClick={() => trackEvent(EVENTS.START_RFQ, { source: 'hero' })}>
                  <Button className="rounded-full bg-[#0061DD] hover:bg-[#004BB3] text-white px-7 py-5 text-base font-semibold shadow-lg shadow-blue-500/20 transition-transform hover:-translate-y-0.5 gap-2" data-testid="hero-get-quote-btn">
                    Get a Quote <ArrowRight className="w-5 h-5" />
                  </Button>
                </Link>
                <Link to="/calculator" onClick={() => trackEvent(EVENTS.OPEN_CALCULATOR, { source: 'hero' })}>
                  <Button variant="outline" className="rounded-full border-[#0061DD] text-[#0061DD] hover:bg-blue-50 px-7 py-5 text-base font-medium gap-2" data-testid="hero-calc-btn">
                    <Calculator className="w-5 h-5" /> Calculate Savings
                  </Button>
                </Link>
                <Link to="/products">
                  <Button variant="outline" className="rounded-full border-stone-200 text-[#57534E] hover:text-[#0061DD] hover:border-[#0061DD] px-7 py-5 text-base font-medium gap-2" data-testid="hero-browse-btn">
                    <ShoppingCart className="w-4 h-4" /> Browse Products
                  </Button>
                </Link>
              </div>
            </div>
            <div className="animate-fade-up stagger-2 flex justify-center">
              <div className="relative w-full max-w-xl mx-auto">
                <img
                  src="/voltide-hero.png"
                  alt="Voltide Battery Energy Storage System"
                  className="w-full rounded-2xl"
                  data-testid="hero-image"
                />
              </div>
            </div>
          </div>
        </div>
        <div className="absolute top-20 right-0 w-96 h-96 bg-blue-100/30 rounded-full blur-3xl -z-10" />
        <div className="absolute bottom-0 left-0 w-72 h-72 bg-amber-100/20 rounded-full blur-3xl -z-10" />
      </section>

      {/* Two Core Solutions */}
      <section className="py-16 lg:py-20 bg-white" data-testid="core-solutions-section">
        <div className="voltide-container">
          <div className="text-center mb-10">
            <Badge className="rounded-full px-4 py-1.5 text-xs font-medium bg-emerald-50 text-emerald-700 border-emerald-100 mb-4">
              Core Solutions
            </Badge>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#1A1A1A]">
              Two ways we save you money
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {CORE_SOLUTIONS.map((sol) => (
              <div key={sol.title} className="bento-card overflow-hidden group" data-testid={`core-solution-${sol.title.toLowerCase().replace(/\s/g, '-')}`}>
                <div className="aspect-[16/9] overflow-hidden bg-[#F9F9F7]">
                  <img src={sol.image} alt={sol.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <h3 className="text-xl font-bold text-[#1A1A1A]">{sol.title}</h3>
                    <Badge className="rounded-full bg-emerald-50 text-emerald-700 border-emerald-100 text-xs">
                      <TrendingDown className="w-3 h-3 mr-1" />Save {formatINRCompact(sol.savings.low)}–{formatINRCompact(sol.savings.high)}/mo
                    </Badge>
                  </div>
                  <p className="text-sm text-[#57534E] leading-relaxed mb-4">{sol.desc}</p>
                  <Link to={sol.link}>
                    <Button variant="outline" className="rounded-full border-[#0061DD] text-[#0061DD] hover:bg-blue-50 gap-2 text-sm">
                      Explore {sol.title} <ArrowRight className="w-4 h-4" />
                    </Button>
                  </Link>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4">
            <AssumptionsFooter />
          </div>
        </div>
      </section>

      {/* Sector Tiles */}
      <section className="py-16 lg:py-20" data-testid="sector-tiles-section">
        <div className="voltide-container">
          <div className="text-center mb-10">
            <Badge className="rounded-full px-4 py-1.5 text-xs font-medium bg-amber-50 text-amber-700 border-amber-100 mb-4">
              Industries We Serve
            </Badge>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#1A1A1A]">
              BESS for every sector
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {SECTORS.map((sector) => (
              <Link to={sector.link} key={sector.title} className="bento-card overflow-hidden group" data-testid={`sector-tile-${sector.title.toLowerCase()}`}>
                <div className="aspect-[4/3] overflow-hidden bg-[#F9F9F7] relative">
                  <img src={sector.image} alt={sector.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                  <div className="absolute bottom-0 left-0 p-4 flex items-center gap-2">
                    <sector.icon className="w-5 h-5 text-white" />
                    <span className="text-white font-semibold text-sm">{sector.title}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Why Voltide - Bento Grid */}
      <section className="py-16 lg:py-20 bg-white" data-testid="why-voltide-section">
        <div className="voltide-container">
          <div className="text-center mb-10">
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#1A1A1A]">
              Why choose Voltide?
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {WHY_ITEMS.map((item, i) => (
              <div key={item.title} className={`bento-card p-6 stagger-${i + 1}`} data-testid={`why-card-${i}`}>
                <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center mb-5">
                  <item.icon className="w-6 h-6 text-[#0061DD]" />
                </div>
                <h3 className="text-lg font-bold text-[#1A1A1A] mb-2">{item.title}</h3>
                <p className="text-sm text-[#57534E] leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 lg:py-20" data-testid="benefits-section">
        <div className="voltide-container">
          <div className="text-center mb-10">
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#1A1A1A]">
              Rent or buy — your choice
            </h2>
            <p className="mt-2 text-base text-[#57534E]">Flexible models for every budget</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {BENEFITS.map((b, i) => (
              <div key={b.title} className="bento-card p-8 text-center" data-testid={`benefit-card-${i}`}>
                <div className="w-14 h-14 rounded-2xl bg-[#0061DD] flex items-center justify-center mx-auto mb-5">
                  <b.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-[#1A1A1A] mb-3">{b.title}</h3>
                <p className="text-sm text-[#57534E] leading-relaxed">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Proof Band */}
      <section className="py-16 lg:py-20 bg-white" data-testid="proof-band-section">
        <div className="voltide-container">
          <div className="text-center mb-10">
            <Badge className="rounded-full px-4 py-1.5 text-xs font-medium bg-blue-50 text-[#0061DD] border-blue-100 mb-4">
              Real Deployments
            </Badge>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#1A1A1A]">
              Powering real businesses today
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <Link to="/case-studies/uru" className="bento-card overflow-hidden group" data-testid="proof-uru-link" onClick={() => trackEvent(EVENTS.VIEW_CASE_STUDY, { study: 'uru' })}>
              <div className="aspect-[16/9] overflow-hidden bg-[#F9F9F7]">
                <img src="/images/uru-hero.jpg" alt="Uru Brewpark - Voltide Case Study" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              </div>
              <div className="p-6">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-[#0061DD]">Restaurant · Bangalore</p>
                    <h3 className="text-lg font-bold text-[#1A1A1A] mt-1">Uru Brewpark</h3>
                    <p className="text-sm text-[#57534E] mt-1">India's first diesel-free large-format restaurant</p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-[#0061DD] shrink-0 group-hover:translate-x-1 transition-transform" />
                </div>
                <div className="flex gap-6 mt-5 pt-5 border-t border-stone-200">
                  <div>
                    <p className="mono text-xl font-bold text-[#1A1A1A]">482 kWh</p>
                    <p className="text-xs text-[#57534E]">Battery capacity</p>
                  </div>
                  <div>
                    <p className="mono text-xl font-bold text-[#1A1A1A]">2</p>
                    <p className="text-xs text-[#57534E]">DG sets replaced</p>
                  </div>
                </div>
              </div>
            </Link>
            <Link to="/case-studies/tfl-betul" className="bento-card overflow-hidden group" data-testid="proof-betul-link" onClick={() => trackEvent(EVENTS.VIEW_CASE_STUDY, { study: 'tfl-betul' })}>
              <div className="aspect-[16/9] overflow-hidden bg-[#F9F9F7]">
                <img src="/images/betul-hero.jpg" alt="Technocraft Fashions Betul - Voltide Case Study" className="w-full h-full object-contain p-3 group-hover:scale-105 transition-transform duration-500" />
              </div>
              <div className="p-6">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-[#0061DD]">Manufacturing · Betul, MP</p>
                    <h3 className="text-lg font-bold text-[#1A1A1A] mt-1">Technocraft Fashions</h3>
                    <p className="text-sm text-[#57534E] mt-1">A battery instead of a diesel generator</p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-[#0061DD] shrink-0 group-hover:translate-x-1 transition-transform" />
                </div>
                <div className="flex gap-6 mt-5 pt-5 border-t border-stone-200">
                  <div>
                    <p className="mono text-xl font-bold text-[#1A1A1A]">100 kW / 96 kWh</p>
                    <p className="text-xs text-[#57534E]">Installed system</p>
                  </div>
                  <div>
                    <p className="mono text-xl font-bold text-[#1A1A1A]">0</p>
                    <p className="text-xs text-[#57534E]">Diesel gensets added</p>
                  </div>
                </div>
              </div>
            </Link>
          </div>
          <div className="text-center mt-8">
            <Link to="/case-studies" className="inline-flex items-center gap-2 text-sm font-medium text-[#0061DD] hover:gap-3 transition-all" data-testid="proof-all-case-studies-link">
              View all case studies <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-16 lg:py-20" data-testid="rental-process-section">
        <div className="voltide-container">
          <div className="text-center mb-10">
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#1A1A1A]">
              How it works
            </h2>
            <p className="mt-2 text-base text-[#57534E]">Four simple steps to clean, reliable power</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {STEPS.map((step, i) => (
              <div key={step.num} className="relative" data-testid={`process-step-${i}`}>
                <div className="bento-card p-6 h-full">
                  <span className="mono text-3xl font-bold text-[#0061DD]/20">{step.num}</span>
                  <h3 className="text-lg font-bold text-[#1A1A1A] mt-3 mb-2">{step.title}</h3>
                  <p className="text-sm text-[#57534E] leading-relaxed">{step.desc}</p>
                </div>
                {i < 3 && <div className="hidden lg:block absolute top-1/2 -right-3 w-6 text-stone-300">
                  <ArrowRight className="w-6 h-6" />
                </div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Band */}
      <section className="py-16 lg:py-20 bg-[#0061DD] relative overflow-hidden" data-testid="cta-section">
        <div className="voltide-container relative z-10 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-white mb-4">
            Ready to switch to clean energy?
          </h2>
          <p className="text-lg text-blue-100 mb-8 max-w-xl mx-auto">
            Get a customized BESS quote for your facility. Rent or buy — no maintenance hassle.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/quote" onClick={() => trackEvent(EVENTS.START_RFQ, { source: 'cta_band' })}>
              <Button className="rounded-full bg-white text-[#0061DD] hover:bg-blue-50 px-8 py-6 text-lg font-semibold gap-2" data-testid="cta-quote-btn">
                Get a Quote <ArrowRight className="w-5 h-5" />
              </Button>
            </Link>
            <Link to="/products">
              <Button variant="outline" className="rounded-full border-white/40 text-white hover:bg-white/10 px-8 py-6 text-lg font-medium gap-2" data-testid="cta-browse-btn">
                <ShoppingCart className="w-4 h-4" /> Browse Products
              </Button>
            </Link>
          </div>
        </div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-blue-400/20 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-72 h-72 bg-blue-600/30 rounded-full blur-3xl" />
      </section>
    </div>
  );
}
